import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SecurityIcon from '@mui/icons-material/Security';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import { format } from 'date-fns';
import { EmpleadoItem, useEmpleadosApi } from '../../../services/api/empleadosApi';
import PageLayout from '../../../components/layout/PageLayout';
import { DarkModeContext } from '../../../context/darkModeContext';

const ACCENT = '#7451f8';

const safeFormat = (date: string) => {
  try { return format(new Date(date), 'dd/MM/yyyy'); } catch { return date || '—'; }
};

const SectionTitle: React.FC<{ label: string; dark: boolean }> = ({ label, dark }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, pl: 1.5, py: 0.5, borderLeft: `3px solid ${ACCENT}`, bgcolor: dark ? 'rgba(116,81,248,0.08)' : 'rgba(116,81,248,0.05)', borderRadius: '0 6px 6px 0' }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: ACCENT, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string | undefined; dark: boolean }> = ({ icon, label, value, dark }) => (
  <Box display="flex" alignItems="flex-start" gap={1.5} py={0.8}>
    <Box sx={{ color: ACCENT, mt: '2px', flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: dark ? '#9b9a97' : '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.9rem', color: dark ? '#e0e0e0' : '#333', mt: '1px' }}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const AfiliadoCard: React.FC<{ icon: React.ReactNode; tipo: string; entidad: string | undefined; dark: boolean }> = ({ icon, tipo, entidad, dark }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '10px', bgcolor: dark ? '#2b2b2b' : '#f7f7f5', border: `1px solid ${dark ? '#3a3a3a' : '#e9e9e7'}` }}>
    <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(116,81,248,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: ACCENT, flexShrink: 0 }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: dark ? '#9b9a97' : '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {tipo}
      </Typography>
      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: dark ? '#e0e0e0' : '#333' }}>
        {entidad || '—'}
      </Typography>
    </Box>
  </Box>
);

const EmpleadoDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { darkMode } = useContext(DarkModeContext);
  const [empleado, setEmpleado] = useState<EmpleadoItem | null>(null);
  const [loading, setLoading] = useState(true);
  const { getById } = useEmpleadosApi();

  useEffect(() => {
    const load = async () => {
      try {
        if (id) {
          const data = await getById(parseInt(id));
          setEmpleado(data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const getImageUrl = (path: string) => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:4020';
    return `${base}${path}`;
  };

  const getInitials = (nombre: string, apellidos: string) =>
    `${nombre?.charAt(0) || ''}${apellidos?.charAt(0) || ''}`.toUpperCase() || '?';

  const card = {
    border: `1px solid ${darkMode ? '#333' : '#e9e9e7'}`,
    borderRadius: '12px',
    bgcolor: darkMode ? '#202020' : '#fff',
    p: 2.5,
  };

  return (
    <PageLayout icon={PersonIcon} title="Detalle de Empleado">
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Botón volver */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/administracion/empleados')}
          sx={{ mb: 2.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: ACCENT, bgcolor: 'rgba(116,81,248,0.08)', '&:hover': { bgcolor: 'rgba(116,81,248,0.15)' } }}
        >
          Volver a Empleados
        </Button>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: ACCENT }} /></Box>
        ) : !empleado ? (
          <Typography color="error">No se encontró el empleado.</Typography>
        ) : (
          <Grid container spacing={2.5}>
            {/* ─── Tarjeta de perfil ─── */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={card}>
                <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                  {/* Foto o avatar */}
                  {empleado.Imagen ? (
                    <Box component="img"
                      src={getImageUrl(empleado.Imagen)}
                      alt={`${empleado.Nombre} ${empleado.Apellidos}`}
                      sx={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${ACCENT}`, flexShrink: 0 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 90, height: 90, bgcolor: ACCENT, fontSize: '2rem', border: `3px solid ${ACCENT}`, flexShrink: 0 }}>
                      {getInitials(empleado.Nombre, empleado.Apellidos)}
                    </Avatar>
                  )}

                  {/* Nombre y datos básicos */}
                  <Box flex={1}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: darkMode ? '#fff' : '#111' }}>
                      {empleado.Nombre} {empleado.Apellidos}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.8} flexWrap="wrap">
                      <Chip
                        label={empleado.Activo ? 'Activo' : 'Inactivo'}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: empleado.Activo ? 'rgba(46,125,50,0.12)' : 'rgba(198,40,40,0.12)', color: empleado.Activo ? '#2e7d32' : '#c62828' }}
                      />
                      {empleado.cargo?.Nombre && (
                        <Chip
                          icon={<WorkIcon sx={{ fontSize: '0.85rem !important' }} />}
                          label={empleado.cargo.Nombre}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: 'rgba(116,81,248,0.1)', color: ACCENT }}
                        />
                      )}
                      {empleado.sucursal?.Nombre && (
                        <Chip
                          icon={<BusinessIcon sx={{ fontSize: '0.85rem !important' }} />}
                          label={empleado.sucursal.Nombre}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Documento */}
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography sx={{ fontSize: '0.72rem', color: darkMode ? '#9b9a97' : '#6b6b6b', textTransform: 'uppercase', fontWeight: 700 }}>
                      {empleado.tipoDocumento?.Nombre || 'Documento'}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: darkMode ? '#e0e0e0' : '#333' }}>
                      {empleado.NumeroDocumento}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* ─── Información Personal ─── */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ ...card, height: '100%' }}>
                <SectionTitle label="Información Personal" dark={darkMode} />
                <InfoRow icon={<BadgeIcon fontSize="small" />} label="Tipo de documento" value={empleado.tipoDocumento?.Nombre || ''} dark={darkMode} />
                <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                <InfoRow icon={<BadgeIcon fontSize="small" />} label="Número de documento" value={empleado.NumeroDocumento} dark={darkMode} />
                <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="Fecha de ingreso" value={safeFormat(empleado.FechaIngreso)} dark={darkMode} />
              </Paper>
            </Grid>

            {/* ─── Información de Contacto ─── */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ ...card, height: '100%' }}>
                <SectionTitle label="Contacto" dark={darkMode} />
                <InfoRow icon={<EmailIcon fontSize="small" />} label="Correo electrónico" value={empleado.Correo} dark={darkMode} />
                <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                <InfoRow icon={<PhoneIcon fontSize="small" />} label="Teléfono" value={empleado.Telefono} dark={darkMode} />
                <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                <InfoRow icon={<HomeIcon fontSize="small" />} label="Dirección" value={empleado.Direccion} dark={darkMode} />
              </Paper>
            </Grid>

            {/* ─── Afiliaciones ─── */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ ...card, height: '100%' }}>
                <SectionTitle label="Afiliaciones" dark={darkMode} />
                <Box display="flex" flexDirection="column" gap={1.2}>
                  <AfiliadoCard icon={<LocalHospitalIcon fontSize="small" />} tipo="EPS" entidad={empleado.eps?.Nombre} dark={darkMode} />
                  <AfiliadoCard icon={<SecurityIcon fontSize="small" />} tipo="ARL" entidad={empleado.arl?.Nombre} dark={darkMode} />
                  <AfiliadoCard icon={<AccountBalanceIcon fontSize="small" />} tipo="AFP" entidad={empleado.afp?.Nombre} dark={darkMode} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </PageLayout>
  );
};

export default EmpleadoDetailView;
